import { FinancialServiceProviderAdapters } from '@seapoint/server/enums';
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { useData } from '../../contexts/data-context/use-data';
import { ErrorSection } from '../../design-system/error-section/error-section';
import { Inline } from '../../design-system/inline/inline';
import { LoadingSection } from '../../design-system/loading/loading';
import { Stack } from '../../design-system/stack/stack';
import { TabsContent } from '../../design-system/tabs/tabs-primitives';
import Header from '../layout/header';
import { PayrollFlowModal } from './components/payroll-flow-modal';
import { IntroducingPayroll } from './empty-states/introducing-payroll';
import { MissingSeapointAccount } from './empty-states/missing-seapoint-account';
import EmployeeTab from './payroll-section-tabs/employee-tab/employee-tab';
import { PayrollSectionTabs } from './payroll-section-tabs/payroll-section-tabs';
import PayrollTabWrapper from './payroll-section-tabs/payroll-tab/payroll-tab-wrapper';
import { usePayrollFlow } from './use-payroll-flow';

export const PayrollPage = () => {
  const location = useLocation();
  const { data, isLoading: isLoadingData, error: dataError } = useData();
  const payrollFlow = usePayrollFlow();
  const { payrollAction, clearPayrollState, updatePayrollAction } = payrollFlow;

  // Handle payment success callback
  useEffect(() => {
    if (data?.payrolls) {
      payrollFlow.handlePaymentSuccessCallback(data.payrolls);
    }
  }, [data?.payrolls, payrollFlow.handlePaymentSuccessCallback]);

  if (isLoadingData) {
    return <LoadingSection text="Loading..." />;
  }

  if (!data) {
    return <ErrorSection error={dataError} />;
  }

  const beneficiaries = data.beneficiaries;

  const employees = data.employees;
  const hasEmployees = employees.length > 0;
  const previousPayrolls = data.payrolls;
  const hasPayrolls = previousPayrolls.length > 0;
  const modulrAccounts = data.accounts.filter(
    (account) => account.fspAdapter === FinancialServiceProviderAdapters.MODULR,
  );
  const hasModulrAccount = modulrAccounts.length > 0;

  const legalEntityCountry = data?.companies[0].legalEntity.country;
  const beneficiariesMatchingLegalEntityCountry = beneficiaries.filter(
    (beneficiary) => legalEntityCountry && beneficiary.country.toLowerCase() === legalEntityCountry.toLowerCase(),
  );
  const employeesMatchingLegalEntityCountry = employees.filter(
    (employee) => legalEntityCountry && employee.country.toLowerCase() === legalEntityCountry.toLowerCase(),
  );

  return (
    <Stack align="stretch" className="min-h-screen p-2.5">
      <Stack align="stretch" className="flex-1 overflow-clip rounded-lg bg-white">
        <div className="overflow-clip rounded-lg">
          <Header key={location.search} />
        </div>

        <Stack align="stretch" className="bg-white px-32" space={10}>
          <Inline justify="between" className="h-[42px]">
            <h1 className="text-2xl font-semibold text-black">Payroll</h1>
          </Inline>
          {hasModulrAccount && hasPayrolls && (
            <PayrollSectionTabs
              triggers={[
                { value: 'PAYROLLS', counterConfig: { showCounter: hasPayrolls, count: previousPayrolls.length } },
                { value: 'EMPLOYEES', counterConfig: { showCounter: hasEmployees, count: employees.length } },
              ]}
            >
              <TabsContent value="PAYROLLS">
                <PayrollTabWrapper payrollFlow={payrollFlow} payrolls={previousPayrolls} />
              </TabsContent>
              <TabsContent value="EMPLOYEES">
                <EmployeeTab employees={employees} />
              </TabsContent>
            </PayrollSectionTabs>
          )}
          {hasModulrAccount && !hasPayrolls && <IntroducingPayroll onSetPayrollAction={updatePayrollAction} />}
          {!hasPayrolls && !hasModulrAccount && <MissingSeapointAccount />}
        </Stack>
        {payrollAction && legalEntityCountry && (
          <PayrollFlowModal
            isOpen={!!payrollAction}
            onClose={clearPayrollState}
            payrollAction={payrollAction}
            companyId={data?.companies[0].id}
            onSetPayrollAction={updatePayrollAction}
            className="h-full w-full overflow-auto bg-seapoint-gray-background-modal"
            legalEntityCountry={legalEntityCountry}
            beneficiaries={beneficiariesMatchingLegalEntityCountry}
            employees={employeesMatchingLegalEntityCountry}
            isLoading={isLoadingData}
            accounts={data?.accounts || []}
            payrollFlow={payrollFlow}
            payrolls={previousPayrolls}
          />
        )}
      </Stack>
    </Stack>
  );
};
